
import React, { useEffect, useMemo, useRef, useState } from "react";
import PageHeader from "../../components/PageHeader/PageHeader";
import { FaEdit, FaSave } from "react-icons/fa";
import CommonTable from "../../components/CommonTable/CommonTable";
import { URLS } from "../../Services/Constant";
import { get, post, put } from "../../Services/ApiHandler";

const Config = () => {
    const [users, setUsers] = useState([]);

    // Pagination
    const [page, setPage] = useState(1);
    const [totalCount, setTotalCount] = useState(0);
    const [pagination, setPagination] = useState(null);

    // Row edit state
    const [editingId, setEditingId] = useState(null);
    const [editValue, setEditValue] = useState("");
    const editValueRef = useRef("");

    // Loading state
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    /**
     * Fetch config data
     */
    const fetchData = async () => {
        try {
            setLoading(true);

            const res = await get(URLS.GETCONFIG);

            console.log("Config response:", res);

            if (res?.status === "success") {
                setUsers(res?.data || []);

                setTotalCount(res?.count || 0);
                setPagination({
                    current_page: res?.current_page || 1,
                    last_page: res?.total_pages || 1,
                });

            } else {
                setUsers([]);
            }
        } catch (error) {
            console.error("Error fetching config:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    /**
     * Click Edit
     * Only the selected row becomes editable.
     */
    const handleEdit = (row) => {
        setEditingId(row?.id);
        const value = row?.value ?? "";
        editValueRef.current = value;
        setEditValue(value);
    };

    /**
     * Cancel editing
     */
    const handleCancel = () => {
        setEditingId(null);
        editValueRef.current = "";
        setEditValue("");
    };

    /**
     * Save edited value
     */
    const handleSubmit = async (row) => {
        if (!row?.id) {
            console.error("Config ID is missing");
            return;
        }

        try {
            setSaving(true);

            const postData = {
                type: row?.id,
                value: editValueRef.current,
            };

            console.log("Post data:", postData);

            const res = await put(URLS.POSTCONFIG, postData);

            console.log("Save response:", res);

            if (res?.status === "success") {
                // Exit edit mode
                setEditingId(null);
                editValueRef.current = "";
                setEditValue("");

                // Refresh table data
                await fetchData();
            }
        } catch (error) {
            console.error("Error updating config:", error);
        } finally {
            setSaving(false);
        }
    };

    /**
     * Table columns
     */
    const columns = useMemo(() => {
        return [
            {
                Header: "S.No",
                accessor: "sno",
                disableSortBy: true,
                width: "10%",
                Cell: ({ row }) => {
                    const serialNumber =
                        (row?.index || 0) + 1 + ((page || 1) - 1) * 10;

                    return (
                        <span className="sno">
                            {String(serialNumber).padStart(2, "0")}
                        </span>
                    );
                },
            },

            {
                Header: "Name",
                accessor: "type",
                width: "35%",
                Cell: ({ value }) => {
                    return (
                        <p className="mb-0">
                            {value || "N/A"}
                        </p>
                    );
                },
            },

            {
                Header: "Value",
                accessor: "value",
                width: "35%",
                Cell: ({ value, row }) => {
                    const rowId = row?.original?.id;

                    const isEditing = editingId === rowId;

                    return (
                        <input
                            type="text"
                            value={
                                isEditing
                                    ? editValueRef.current
                                    : value ?? ""
                            }
                            onChange={(e) => {
                                if (isEditing) {
                                    editValueRef.current = e.target.value;
                                    setEditValue(e.target.value);
                                }
                            }}
                            readOnly={!isEditing}
                            className="fcontrol"
                            style={{
                                backgroundColor: isEditing
                                    ? "#fff"
                                    : "#f5f5f5",
                                cursor: isEditing
                                    ? "text"
                                    : "default",
                                border: isEditing
                                    ? "1px solid #7a52ab"
                                    : "1px solid #ddd",
                            }}
                        />
                    );
                },
            },

            {
                Header: "Action",
                accessor: "action",
                disableSortBy: true,
                width: "20%",
                Cell: ({ row }) => {
                    const rowData = row?.original;
                    const rowId = rowData?.id;

                    const isEditing = editingId === rowId;

                    return (
                        <div
                            style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                            }}
                        >
                            {!isEditing ? (
                                <button
                                    type="button"
                                    className="action-btn"
                                    onClick={() => handleEdit(rowData)}
                                    title="Edit"
                                    disabled={saving}
                                >
                                    <FaEdit />
                                </button>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        className="action-btn"
                                        onClick={() =>
                                            handleSubmit(rowData)
                                        }
                                        title="Save"
                                        disabled={saving}
                                    >
                                        <FaSave />
                                    </button>

                                    <button
                                        type="button"
                                        className="action-btn"
                                        onClick={handleCancel}
                                        title="Cancel"
                                        disabled={saving}
                                    >
                                        ✕
                                    </button>
                                </>
                            )}
                        </div>
                    );
                },
            },
        ];
    }, [page, editingId, saving]);

    return (
        <>
            <PageHeader title="Config" />

            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginBottom: "20px",
                }}
            >
                <h2
                    style={{
                        fontSize: "20px",
                        fontWeight: "600",
                        color: "#7a52ab",
                        margin: 0,
                    }}
                >
                    Total: {totalCount || 0}
                </h2>
            </div>

            <CommonTable
                columns={columns}
                data={users || []}
                pagecount={pagination}
                setPageno={setPage}
                pageno={page || 1}
            />

            {loading && (
                <div
                    style={{
                        textAlign: "center",
                        marginTop: "15px",
                    }}
                >
                    Loading...
                </div>
            )}
        </>
    );
};

export default Config;
